import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HandlersProjectResponse } from '../athenaApiGen';

type TFormData = {
  title: string;
  projectCode: string;
  programId: string;
  ownerId: string;
  locationId: string;
  description: string;
  projectFilePath: string;
  attachmentNames: string[]; // Store only file names, not File objects
  creatorContactId: string; // UUID of the contact creating attachments
  selectedDeliverableTypes: string[]; // Array of deliverableTypeIds selected for the project
};

type TFormErrors = {
  [key: string]: string;
};

type TAttachmentUploadStatus = {
  filename: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
};

type TProjectsState = {
  activeStep: number;
  formData: TFormData;
  errors: TFormErrors;
  isSubmitting: boolean;
  programName: string;
  ownerName: string;
  locationName: string;
  isModalOpen: boolean;
  modalMode: 'create' | 'edit';
  editingProjectId: string | null;
  lastCreatedProject: HandlersProjectResponse | null;
  attachmentUploadStatus: TAttachmentUploadStatus[]; // Track upload status for each attachment
  existingAttachments: any[]; // Existing attachments in edit mode
  deletedAttachmentIds: { id: string; name: string }[]; // IDs of attachments marked for deletion
  originalDeliverableTypeIds: string[]; // Original deliverable type IDs when entering edit mode
  originalDeliverableTypes: any[]; // Full deliverable type objects for display in Review
  // Pagination state for projects table
  page: number;
  pageSize: number;
  search: string; // Search query for projects table
  // Filter state for projects table
  programId: string; // Filter by program ID
  ownerId: string; // Filter by owner ID
};

const initialFormData: TFormData = {
  title: '',
  projectCode: '',
  programId: '',
  ownerId: '',
  locationId: '',
  description: '',
  projectFilePath: '',
  attachmentNames: [],
  creatorContactId: '',
  selectedDeliverableTypes: [],
};

const initialState: TProjectsState = {
  activeStep: 0,
  formData: initialFormData,
  errors: {},
  isSubmitting: false,
  programName: '',
  ownerName: '',
  locationName: '',
  isModalOpen: false,
  modalMode: 'create',
  editingProjectId: null,
  lastCreatedProject: null,
  attachmentUploadStatus: [],
  existingAttachments: [],
  deletedAttachmentIds: [],
  originalDeliverableTypeIds: [],
  originalDeliverableTypes: [],
  page: 0,
  pageSize: 25,
  search: '',
  programId: '',
  ownerId: '',
};

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setActiveStep: (state, action: PayloadAction<number>) => {
      state.activeStep = action.payload;
    },
    setFormData: (state, action: PayloadAction<Partial<TFormData>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetFormData: (state) => {
      state.formData = initialFormData;
    },
    setErrors: (state, action: PayloadAction<TFormErrors>) => {
      state.errors = action.payload;
    },
    clearErrors: (state) => {
      state.errors = {};
    },
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    setProgramName: (state, action: PayloadAction<string>) => {
      state.programName = action.payload;
    },
    setOwnerName: (state, action: PayloadAction<string>) => {
      state.ownerName = action.payload;
    },
    setLocationName: (state, action: PayloadAction<string>) => {
      state.locationName = action.payload;
    },
    setModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },
    openModal: (state) => {
      state.isModalOpen = true;
      state.modalMode = 'create';
      state.editingProjectId = null;
    },
    closeModal: (state) => {
      state.isModalOpen = false;
    },
    openEditModal: (state, action: PayloadAction<{ projectId: string; projectData: Partial<TFormData> }>) => {
      state.isModalOpen = true;
      state.modalMode = 'edit';
      state.editingProjectId = action.payload.projectId;
      state.formData = { ...initialFormData, ...action.payload.projectData };
      state.activeStep = 0;
    },
    setModalMode: (state, action: PayloadAction<'create' | 'edit'>) => {
      state.modalMode = action.payload;
    },
    setEditingProjectId: (state, action: PayloadAction<string | null>) => {
      state.editingProjectId = action.payload;
    },
    setLastCreatedProject: (state, action: PayloadAction<HandlersProjectResponse>) => {
      state.lastCreatedProject = action.payload;
    },
    clearLastCreatedProject: (state) => {
      state.lastCreatedProject = null;
    },
    resetProjectModal: (state) => {
      state.activeStep = 0;
      state.formData = initialFormData;
      state.errors = {};
      state.isSubmitting = false;
      state.programName = '';
      state.ownerName = '';
      state.locationName = '';
      state.isModalOpen = false;
      state.modalMode = 'create';
      state.editingProjectId = null;
      state.attachmentUploadStatus = [];
      state.existingAttachments = [];
      state.deletedAttachmentIds = [];
      state.originalDeliverableTypeIds = [];
      state.originalDeliverableTypes = [];
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.page = 0; // Reset to first page when search changes
    },
    setProgramId: (state, action: PayloadAction<string>) => {
      state.programId = action.payload;
      state.page = 0; // Reset to first page when filter changes
    },
    setOwnerId: (state, action: PayloadAction<string>) => {
      state.ownerId = action.payload;
      state.page = 0; // Reset to first page when filter changes
    },
    // Attachment upload status actions
    initializeAttachmentStatus: (state, action: PayloadAction<string[]>) => {
      state.attachmentUploadStatus = action.payload.map(filename => ({
        filename,
        status: 'pending',
      }));
    },
    updateAttachmentStatus: (state, action: PayloadAction<{ filename: string; status: TAttachmentUploadStatus['status']; error?: string }>) => {
      const attachment = state.attachmentUploadStatus.find(a => a.filename === action.payload.filename);
      if (attachment) {
        attachment.status = action.payload.status;
        if (action.payload.error) {
          attachment.error = action.payload.error;
        }
      }
    },
    clearAttachmentStatus: (state) => {
      state.attachmentUploadStatus = [];
    },
    setExistingAttachments: (state, action: PayloadAction<any[]>) => {
      state.existingAttachments = action.payload;
    },
    setDeletedAttachmentIds: (state, action: PayloadAction<{id: string; name: string}[]>) => {
      state.deletedAttachmentIds = action.payload;
    },
    addDeletedAttachmentId: (state, action: PayloadAction<{id: string; name: string}>) => {
      if (!state.deletedAttachmentIds.some(a => a.id === action.payload.id)) {
        state.deletedAttachmentIds.push(action.payload);
      }
    },
    setOriginalDeliverableTypeIds: (state, action: PayloadAction<string[]>) => {
      state.originalDeliverableTypeIds = action.payload;
    },
    setOriginalDeliverableTypes: (state, action: PayloadAction<any[]>) => {
      state.originalDeliverableTypes = action.payload;
    },
  },
});

export const {
  setActiveStep,
  setFormData,
  resetFormData,
  setErrors,
  clearErrors,
  setIsSubmitting,
  setProgramName,
  setOwnerName,
  setLocationName,
  setModalOpen,
  openModal,
  closeModal,
  openEditModal,
  setModalMode,
  setEditingProjectId,
  setLastCreatedProject,
  clearLastCreatedProject,
  resetProjectModal,
  setPage,
  setPageSize,
  setSearch,
  setProgramId,
  setOwnerId,
  initializeAttachmentStatus,
  updateAttachmentStatus,
  clearAttachmentStatus,
  setExistingAttachments,
  setDeletedAttachmentIds,
  addDeletedAttachmentId,
  setOriginalDeliverableTypeIds,
  setOriginalDeliverableTypes,
} = projectsSlice.actions;

export default projectsSlice.reducer;
